/**
 * Los tamaños de letra grandes dejan de desbordar el teléfono.
 *
 * ## Qué pasaba
 *
 * La barra del editor guarda el tamaño como `font-size: 48px` DENTRO del HTML
 * del texto, y 48px es una medida elegida mirando el canvas de 1280. En un
 * teléfono de 390px el mismo título ocupa dos y tres líneas y se come la
 * pantalla: medido en la home de B2 Autos, "Nuestras Marcas 0 km" y "Vehículos
 * Usados" salían a 48px y 108px de alto cada uno.
 *
 * ## Por qué un tope en `vw` y no un breakpoint
 *
 * `min(48px, 8.5vw)` es el tamaño de siempre mientras la pantalla sea lo
 * bastante ancha, y recién por debajo empieza a ceder — para 48px eso pasa a
 * partir de 565px de viewport. No hay salto en ningún ancho: la letra encoge de
 * forma continua al arrastrar, y arriba de ese punto el valor es exactamente el
 * que el dealer eligió. En escritorio no cambia un píxel.
 *
 * Y SOLO puede achicar: `min()` nunca devuelve más que el valor guardado.
 *
 * ## Por qué desde `PISO_PX` para arriba
 *
 * Un cuerpo de texto de 16 o 20px ya entra bien en un teléfono, y meterle un
 * `min()` sería ensuciar el HTML sin cambiar nada (8.5vw sólo bajaría de 20px
 * en pantallas de menos de 235px, que no existen). El tope es para los títulos.
 *
 * ## Por qué se aplica DESPUÉS de sanitizar
 *
 * El filtro de `htmlSeguro` valida cada valor de CSS contra `cssfilter`, que no
 * conoce `min()` y lo tiraría. Acá se reescribe la salida ya limpia, y sólo
 * dentro de un atributo `style`, así que un `font-size:48px` que fuera texto
 * visible no se toca.
 */

/** Desde este tamaño en adelante se le pone tope. Abajo no hace falta. */
const PISO_PX = 25;

/**
 * El tope, en porcentaje del ancho de la ventana.
 *
 * Calibrado contra b2autos.cl, el sitio de referencia: su título "Nuestras
 * Marcas" mide 29px a 390px de viewport. 7.4vw da exactamente eso para un
 * título guardado en 48px, y desde 649px de viewport el tope ya no muerde.
 */
const TOPE_VW = 7.4;

/** `font-size: 48px` → `font-size: min(48px, 8.5vw)`, sólo si supera el piso. */
const conTope = (declaraciones: string): string =>
  declaraciones.replace(
    /font-size\s*:\s*(\d+(?:\.\d+)?)px/gi,
    (entera, px) => (Number(px) >= PISO_PX ? `font-size:min(${px}px, ${TOPE_VW}vw)` : entera),
  );

/**
 * Aplica el tope a todos los `style` de un HTML YA SANITIZADO.
 *
 * Devuelve la misma cadena cuando no hay nada que topear, así que un texto sin
 * tamaños grandes sale carácter por carácter igual que antes.
 */
export const conTipografiaMovil = (html: string): string =>
  html.replace(/style="([^"]*)"/gi, (entera, cuerpo) => {
    const nuevo = conTope(cuerpo);
    return nuevo === cuerpo ? entera : `style="${nuevo}"`;
  });

/** Lo que se le pasa a `dangerouslySetInnerHTML` en `Text` y `Heading`. */
export const htmlConTipografiaMovil = (html: { __html: string }): { __html: string } => ({
  __html: conTipografiaMovil(html.__html),
});
